import { useEffect } from 'react'
import { useAppDispatch } from 'src/store/hooks'

type AsyncStatus = 'uninitialized' | 'pending' | 'fulfilled' | 'rejected'

/**
 * Hook to automatically fetch data when Redux status is 'uninitialized'
 *
 * Replaces the common pattern:
 * ```
 * useEffect(() => {
 *   if (status === 'uninitialized') {
 *     dispatch(fetchAction())
 *   }
 * }, [status, dispatch])
 * ```
 *
 * @example
 * useAutoFetch(groupsStatus, fetchGroups)
 * useAutoFetch(linkedServicesStatus, fetchLinkedServices)
 * useAutoFetch(coursesStatus, fetchCourses, [linkedServiceID])
 *
 * @param status - The Redux async status to check
 * @param fetchAction - The Redux thunk action to dispatch
 * @param dependencies - Optional additional dependencies for the useEffect
 */
export function useAutoFetch(
  status: AsyncStatus,
  fetchAction: any,
  dependencies: any[] = [],
) {
  let dispatch = useAppDispatch()

  useEffect(() => {
    if (status === 'uninitialized') {
      dispatch(fetchAction())
    }
  }, [status, dispatch, fetchAction, ...dependencies])
}
